import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "../App";
import { ArrowLeft, Heart, CheckCircle, XCircle, FileText, Eye, DollarSign, Users, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

export default function AdminFundFamily() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [pool, setPool] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [proofModal, setProofModal] = useState(null);

  const fetchApps = async () => {
    try {
      const res = await api.get(`/fund-family/admin/applications?status=${filter}`);
      setApps(res.data.applications || []);
      setPool(res.data.pool_balance_usd || 0);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("Admin access required");
        navigate("/dashboard");
      }
    }
    setLoading(false);
  };

  useEffect(() => { fetchApps(); }, [filter]);

  const approve = async (appId, name) => {
    if (!window.confirm(`Approve ${name}? This will deduct $21 from the pool and grant them Standard tier access.`)) return;
    try {
      await api.post(`/fund-family/admin/approve/${appId}`);
      toast.success(`${name} has been funded!`);
      fetchApps();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Approval failed");
    }
  };

  const deny = async (appId, name) => {
    if (!window.confirm(`Deny ${name}'s application?`)) return;
    try {
      await api.post(`/fund-family/admin/deny/${appId}`);
      toast.success("Application denied");
      fetchApps();
    } catch (err) {
      toast.error("Deny failed");
    }
  };

  const viewProof = async (appId) => {
    try {
      const res = await api.get(`/fund-family/admin/proof/${appId}`);
      setProofModal(res.data);
    } catch {
      toast.error("No proof document found");
    }
  };

  const pending = apps.filter(a => a.status === "pending").length;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#F7931A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="admin-fund-family-page">
      <header className="sticky top-0 z-50 glass-darker border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <button onClick={() => navigate("/dashboard")} className="text-zinc-400 hover:text-white" data-testid="admin-back-btn">
            <ArrowLeft size={20} />
          </button>
          <h1 className="font-heading text-xl font-bold text-white">
            <Heart className="inline mr-2 text-[#F7931A]" size={20} />
            Fund a Family — <span className="text-[#F7931A]">Admin</span>
          </h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Pool stats */}
        <div className="grid grid-cols-3 gap-3" data-testid="pool-stats">
          <div className="card-dark p-4 text-center">
            <DollarSign className="text-[#F7931A] mx-auto mb-1" size={20} />
            <p className="text-white font-mono text-xl font-bold" data-testid="pool-balance">${pool}</p>
            <p className="text-zinc-500 text-xs">Pool Balance</p>
          </div>
          <div className="card-dark p-4 text-center">
            <Clock className="text-yellow-400 mx-auto mb-1" size={20} />
            <p className="text-white font-mono text-xl font-bold">{pending}</p>
            <p className="text-zinc-500 text-xs">Pending</p>
          </div>
          <div className="card-dark p-4 text-center">
            <Users className="text-green-400 mx-auto mb-1" size={20} />
            <p className="text-white font-mono text-xl font-bold">
              {Math.floor(pool / 21)}
            </p>
            <p className="text-zinc-500 text-xs">Can Fund</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-2">
          {["pending", "approved", "denied", "all"].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-sm capitalize ${
                filter === f ? "bg-[#F7931A] text-black font-bold" : "bg-[#161616] text-zinc-500"
              }`}
              data-testid={`filter-${f}`}>
              {f}
            </button>
          ))}
        </div>

        {/* Applications */}
        {apps.length === 0 ? (
          <div className="card-dark p-8 text-center" data-testid="no-applications">
            <Heart className="text-zinc-700 mx-auto mb-3" size={40} />
            <p className="text-zinc-400 text-sm">No {filter} applications</p>
          </div>
        ) : (
          <div className="space-y-3">
            {apps.map((app, i) => (
              <div key={app.id} className="card-dark p-5" data-testid={`application-${i}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="text-white font-medium">{app.applicant_name}</p>
                    <p className="text-zinc-500 text-xs">{app.applicant_email}</p>
                    <p className="text-zinc-600 text-[10px] mt-0.5">
                      Applied {new Date(app.created_at).toLocaleDateString()} — Household: {app.household_size}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      app.status === "pending" ? "bg-yellow-500/20 text-yellow-400" :
                      app.status === "approved" ? "bg-green-500/20 text-green-400" :
                      "bg-red-500/20 text-red-400"
                    }`}>
                      {app.status}
                    </span>
                    {app.has_proof && (
                      <button onClick={() => viewProof(app.id)}
                        className="text-[#F7931A] hover:text-[#F7931A]/70 p-1" data-testid={`view-proof-${i}`}>
                        <Eye size={16} />
                      </button>
                    )}
                  </div>
                </div>

                <div className="bg-[#0A0A0A] rounded-lg p-3 mb-3">
                  <p className="text-zinc-500 text-xs mb-1">Their story:</p>
                  <p className="text-zinc-300 text-sm italic">"{app.why_cant_afford}"</p>
                </div>

                {app.how_heard && (
                  <p className="text-zinc-600 text-xs mb-3">How heard: {app.how_heard}</p>
                )}

                <div className="flex items-center gap-2">
                  <span className={`text-xs ${app.has_proof ? "text-green-400" : "text-zinc-600"}`}>
                    <FileText className="inline mr-1" size={12} />
                    {app.has_proof ? "Proof uploaded" : "No proof"}
                  </span>
                </div>

                {app.status === "pending" && (
                  <div className="flex gap-2 mt-3 pt-3 border-t border-white/5">
                    <Button onClick={() => approve(app.id, app.applicant_name)}
                      disabled={pool < 21}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white" data-testid={`approve-${i}`}>
                      <CheckCircle size={16} className="mr-1" />
                      {pool >= 21 ? "Approve ($21 from pool)" : "Pool too low"}
                    </Button>
                    <Button onClick={() => deny(app.id, app.applicant_name)}
                      variant="ghost" className="text-red-400 hover:text-red-300" data-testid={`deny-${i}`}>
                      <XCircle size={16} className="mr-1" /> Deny
                    </Button>
                  </div>
                )}

                {app.status === "approved" && app.reviewed_at && (
                  <p className="text-green-400/70 text-xs mt-2">
                    Approved on {new Date(app.reviewed_at).toLocaleDateString()}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Proof modal */}
      {proofModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => setProofModal(null)}>
          <div className="bg-[#0A0A0A] border border-[#F7931A]/30 rounded-2xl p-4 max-w-lg w-full"
            onClick={e => e.stopPropagation()} data-testid="proof-modal">
            <p className="text-white font-medium mb-3">Proof Document: {proofModal.proof_filename}</p>
            <img
              src={`data:image/jpeg;base64,${proofModal.proof_data}`}
              alt="Proof document"
              className="w-full rounded-lg mb-3"
            />
            <Button onClick={() => setProofModal(null)} variant="ghost" className="w-full text-zinc-400">
              Close
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
